import type {FileUpload} from "./FileUpload";
import {DicomTags} from "../network/contracts/DicomDataSet";
import {newShortGuid} from "../util/types/guid";
import {parseDate} from "../util/date/parseDate";
import {formatOrDefault} from "../util/date/formatOrDefault";
import { action, makeObservable, observable } from "mobx";

export interface IStudyInUpload {
    studyGuid: string;
    filesToUpload: FileUpload[];
    filesToUploadSize: number;
    studyInstanceUid: string;
    accessionNumber: string;
    studyDate: string;
    studyDateFormatted: string;
    studyTime: string;
    studyTimeFormatted: string;
    modality: string;
    studyDescription: string;
    seriesDescription: string;
}

export class StudyInUpload implements IStudyInUpload {
    public studyGuid: string;

    public filesToUpload: FileUpload[] = [];
    
    @observable
    public comment: string | null = null;

    constructor(filesToUpload: FileUpload[]) {
        makeObservable(this);

        this.filesToUpload = filesToUpload;
        this.studyGuid = newShortGuid();
    }
    
    @action
    public setComment(comment: string | null) {
        this.comment = comment;
    }

    public get filesToUploadSize(): number {
        let filesToUploadTotalSize = 0;

        this.filesToUpload.forEach((f) => {
            filesToUploadTotalSize += f.size;
        });

        return filesToUploadTotalSize;
    }

    public get studyInstanceUid(): string {
        if (!this.filesToUpload || this.filesToUpload.length === 0) return "";
        return this.filesToUpload[0].getDicomTagValue(DicomTags.StudyInstanceUID);
    }

    public get accessionNumber(): string {
        if (!this.filesToUpload || this.filesToUpload.length === 0) return "";
        return this.filesToUpload[0].getDicomTagValue(DicomTags.AccessionNumber);
    }

    public get studyDate(): string {
        if (!this.filesToUpload || this.filesToUpload.length === 0) return "";
        return this.filesToUpload[0].getDicomTagValue(DicomTags.StudyDate);
    }

    public get studyDateFormatted(): string {
        const studyDate = this.studyDate;
        const date = parseDate(studyDate, "yyyyMMdd");
        return formatOrDefault(date, "dd/MM/yyyy", studyDate);
    }

    public get studyTime(): string {
        if (!this.filesToUpload || this.filesToUpload.length === 0) return "";
        return this.filesToUpload[0].getDicomTagValue(DicomTags.StudyTime);
    }

    public get studyTimeFormatted(): string {
        const studyTime = this.studyTime;
        const date = parseDate(studyTime, "HHmmss");
        return formatOrDefault(date, "HH:mm", studyTime);
    }

    public get modality(): string {
        if (!this.filesToUpload || this.filesToUpload.length === 0) return "";
        return this.filesToUpload[0].getDicomTagValue(DicomTags.Modality);
    }

    public get studyDescription(): string {
        if (!this.filesToUpload || this.filesToUpload.length === 0) return "";
        return this.filesToUpload[0].getDicomTagValue(DicomTags.StudyDescription);
    }

    public get seriesDescription(): string {
        if (!this.filesToUpload || this.filesToUpload.length === 0) return "";
        return this.filesToUpload[0].getDicomTagValue(DicomTags.SeriesDescription);
    }
}
